/**
 * GHG monthly breakdown table used by CompanyGHGPage.
 * Renders a loading skeleton or the actual data table with a shared column header,
 * eliminating the duplicate header that previously appeared in both states.
 */

import React from 'react';
import {
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { formatNumber } from '../../utils/numberFormat';
import { formatEmissions } from './ghgStatsUtils';

interface MonthRow {
  month: number;
  year: number;
  month_name: string;
  total_tot_emissions: number;
  total_ops_emissions: number;
  total_ene_emissions: number;
  total_ei: number;
  total_tkm: number;
  ytd_tot_emissions: number;
}

interface Props {
  loading: boolean;
  months: MonthRow[];
  selectedMonth: { year: number; month: number } | null;
  onMonthClick: (month: { year: number; month: number; month_name: string }) => void;
}

const GhgMonthlyTable: React.FC<Props> = ({ loading, months, selectedMonth, onMonthClick }) => {
  const columns = (
    <TableRow>
      <TableCell>Month</TableCell>
      <TableCell align="right">TOT (Tonnes)</TableCell>
      <TableCell align="right">OPS (Tonnes)</TableCell>
      <TableCell align="right">ENE (Tonnes)</TableCell>
      <TableCell align="right">EI (gr/tkm)</TableCell>
      <TableCell align="right">TKM</TableCell>
      <TableCell align="right">YTD TOT (Tonnes)</TableCell>
    </TableRow>
  );

  return (
    <Paper sx={{ p: 3 }}>
      {loading ? (
        <>
          <Skeleton variant="text" width={200} sx={{ fontSize: '1.25rem', mb: 2 }} />
          <TableContainer>
            <Table size="small">
              <TableHead>{columns}</TableHead>
              <TableBody>
                {Array.from({ length: 8 }, (_, i) => `monthly-skeleton-${i}`).map((rowKey) => (
                  <TableRow key={rowKey}>
                    <TableCell><Skeleton variant="text" width={90} /></TableCell>
                    <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }} /></TableCell>
                    <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }} /></TableCell>
                    <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }} /></TableCell>
                    <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }} /></TableCell>
                    <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }} /></TableCell>
                    <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Monthly Emissions
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>{columns}</TableHead>
              <TableBody>
                {months.map((month) => (
                  <TableRow
                    key={month.month}
                    hover
                    onClick={() => onMonthClick(month)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {month.month_name}
                        {selectedMonth?.year === month.year && selectedMonth?.month === month.month && ' ▼'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatEmissions(month.total_tot_emissions)}</TableCell>
                    <TableCell align="right">{formatEmissions(month.total_ops_emissions)}</TableCell>
                    <TableCell align="right">{formatEmissions(month.total_ene_emissions)}</TableCell>
                    <TableCell align="right">{formatNumber(month.total_ei, 2)}</TableCell>
                    <TableCell align="right">{formatNumber(month.total_tkm)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                        {formatEmissions(month.ytd_tot_emissions)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
};

export default GhgMonthlyTable;
